/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { regionLabel } from "../data/crm";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { TrendingDown, TrendingUp, Minus, Loader2, Send } from "lucide-react";
import { toast } from "../components/ui/sonner";

const COLORS = ["#f43f5e", "#f97316", "#eab308", "#8b5cf6", "#0ea5e9", "#14b8a6", "#64748b", "#a855f7"];

export default function CRMLostAnalytics() {
  const { api, lang, user } = useApp();
  const zh = lang === "zh";
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [insights, setInsights] = useState(null);
  useEffect(() => {
    api.get("/crm/lost-analytics").then(({ data }) => setData(data)).catch(() => setData({ total: 0, byReason: [], byRegion: [], recent: [] }));
    api.get("/crm/churn-insights").then(({ data }) => setInsights(data)).catch(() => setInsights(null));
  }, []); // eslint-disable-line
  const canReport = ["admin", "ceo", "marketing", "sales_vp", "sales_manager"].includes(user.role);
  const sendReport = async () => {
    setSending(true);
    try { const { data } = await api.post("/crm/churn-weekly-report/run", {});
      toast.success(zh ? `周报已推送给 ${data.emailRecipients} 位管理层` : `Sent to ${data.emailRecipients}`);
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "推送失败" : "Failed")); }
    finally { setSending(false); }
  };

  if (!data) return <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const reasonData = (data.byReason || []).map((r) => ({ name: r.label, count: r.count }));
  const regionData = (data.byRegion || []).map((r) => ({ name: regionLabel(r.region, zh), count: r.count }));

  return (
    <div className="max-w-6xl mx-auto" data-testid="crm-lost-analytics">
      <div className="flex-wrap md:flex-nowrap flex items-center gap-2.5 mb-5">
        <div className="h-9 w-9 rounded-xl bg-rose-100 flex items-center justify-center"><TrendingDown className="h-5 w-5 text-rose-600" /></div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{zh ? "客户流失分析" : "Churn Analysis"}</h1>
          <p className="text-[13px] text-slate-500">{zh ? "已被市场部经理定为无效的客户，按原因与区域统计。" : "Finalized-invalid leads by reason & region."}</p>
        </div>
        <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-4">
          {canReport && (
            <button onClick={sendReport} disabled={sending} data-testid="send-weekly-report"
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 h-9 px-3.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              <Send className="h-3.5 w-3.5" />{sending ? (zh ? "推送中…" : "…") : (zh ? "推送本周流失周报" : "Send weekly report")}
            </button>
          )}
          <div className="text-right">
            <p className="text-3xl font-black text-rose-600" data-testid="lost-total">{data.total}</p>
            <p className="text-[11px] text-slate-400 uppercase tracking-wide">{zh ? "累计流失" : "Total lost"}</p>
          </div>
        </div>
      </div>

      {data.total === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-slate-400" data-testid="lost-empty">
          <TrendingDown className="h-10 w-10 mb-2" /><p className="text-sm">{zh ? "暂无流失客户数据" : "No churn data yet"}</p>
        </div>
      ) : (
        <>
          {data.comparison && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mb-5" data-testid="lost-trend">
              <p className="text-[14px] font-bold text-slate-700 mb-3">{zh ? "月度流失趋势" : "Monthly Trend"}</p>
              <div className="grid lg:grid-cols-[280px_1fr] gap-5 items-center">
                <div className="rounded-xl bg-slate-50 p-4" data-testid="lost-compare">
                  {(() => {
                    const c = data.comparison;
                    const up = c.delta > 0, flat = c.delta === 0;
                    const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
                    const col = flat ? "text-slate-500" : up ? "text-rose-600" : "text-emerald-600";
                    return (
                      <>
                        <div className="flex-wrap md:flex-nowrap flex items-end gap-3">
                          <div><p className="text-[11px] text-slate-400">{zh ? "本月流失" : "This month"}</p><p className="text-3xl font-black text-slate-800" data-testid="compare-this">{c.thisCount}</p></div>
                          <div className={`flex items-center gap-1 font-bold ${col} mb-1`} data-testid="compare-delta">
                            <Icon className="h-4 w-4" />{up ? "+" : ""}{c.delta}（{c.pct > 0 ? "+" : ""}{c.pct}%）
                          </div>
                        </div>
                        <p className="text-[12px] text-slate-500 mt-1">{zh ? "上月" : "Last month"}：{c.lastCount}</p>
                        <p className="text-[12px] mt-2 leading-relaxed">
                          {zh ? (up ? `⚠️ 本月流失较上月增加，主要卡在「${c.thisTopReason?.label || "—"}」。`
                                    : flat ? `本月与上月持平，主因「${c.thisTopReason?.label || "—"}」。`
                                    : `✅ 本月流失较上月下降，情况好转。`)
                              : (up ? "Churn up vs last month." : flat ? "Flat vs last month." : "Churn down vs last month.")}
                        </p>
                        {c.thisTopReason && <span className="inline-block mt-1.5 text-[11px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-600">{zh ? "本月主因：" : "Top: "}{c.thisTopReason.label}（{c.thisTopReason.count}）</span>}
                      </>
                    );
                  })()}
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={(data.monthly || []).map((m) => ({ name: m.month.slice(5), count: m.count }))} margin={{ left: 0, right: 12 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip cursor={{ fill: "#f8fafc" }} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={36} fill="#4F6BFF" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {insights && insights.total > 0 && (
            <div className="rounded-2xl border border-indigo-200 bg-white p-4 shadow-sm mb-5" data-testid="churn-insights">
              <p className="text-[14px] font-bold text-slate-700 mb-1">🧭 {zh ? "决策洞察 · 数据驱动优化" : "Decision Insights"}</p>
              <p className="text-[12px] text-slate-500 mb-3">{zh ? "将流失原因映射到 4 个决策维度，并给出 AI 优化建议。" : "Churn reasons mapped to 4 decision levers with AI advice."}</p>
              {insights.overall && (
                <div className="mb-3 rounded-xl bg-indigo-50 border-l-4 border-indigo-500 px-3.5 py-2.5" data-testid="churn-insights-overall">
                  <p className="text-[12px] font-bold text-indigo-700 mb-0.5">🤖 {zh ? "总体决策简报" : "Overall brief"}</p>
                  <p className="text-[13px] text-slate-700 leading-relaxed">{insights.overall}</p>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                {insights.dimensions.map((dim) => (
                  <div key={dim.key} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid={`insight-dim-${dim.key}`}>
                    <div className="flex-wrap md:flex-nowrap flex items-center justify-between">
                      <p className="text-[13.5px] font-bold text-slate-800">{dim.icon} {dim.label}</p>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${dim.score >= 3 ? "bg-rose-100 text-rose-600" : dim.score > 0 ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-400"}`}>{zh ? `命中 ${dim.score}` : dim.score}</span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5">{zh ? "关注：" : "Focus: "}{dim.focus}</p>
                    {dim.reasons.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {dim.reasons.map((r) => <span key={r.key} className="text-[11px] px-1.5 py-0.5 rounded-full bg-white border border-slate-200 text-slate-500">{r.label} {r.count}</span>)}
                      </div>
                    )}
                    {dim.recommendation && <p className="text-[12.5px] text-slate-700 leading-relaxed mt-2 pt-2 border-t border-slate-100">💡 {dim.recommendation}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="lost-by-reason-chart">
              <p className="text-[14px] font-bold text-slate-700 mb-3">{zh ? "流失原因分布" : "By Reason"}</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={reasonData} layout="vertical" margin={{ left: 12, right: 20 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" width={92} tick={{ fontSize: 12 }} />
                  <Tooltip cursor={{ fill: "#f8fafc" }} />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>
                    {reasonData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="lost-by-region-chart">
              <p className="text-[14px] font-bold text-slate-700 mb-3">{zh ? "各区域流失数" : "By Region"}</p>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={regionData} margin={{ left: 0, right: 12 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip cursor={{ fill: "#f8fafc" }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={44} fill="#f43f5e" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mt-5" data-testid="lost-recent">
            <p className="text-[14px] font-bold text-slate-700 mb-3">{zh ? "最近流失客户" : "Recent Churn"}</p>
            <div className="overflow-x-auto">
              <ResponsiveTable className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                    <th className="py-2 pr-3">{zh ? "客户" : "Client"}</th>
                    <th className="py-2 pr-3">{zh ? "区域" : "Region"}</th>
                    <th className="py-2 pr-3">{zh ? "原因" : "Reason"}</th>
                    <th className="py-2 pr-3">{zh ? "备注" : "Note"}</th>
                    <th className="py-2 pr-3">{zh ? "标记人" : "By"}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recent || []).map((r, i) => (
                    <tr key={i} className="border-b border-slate-50" data-testid={`lost-recent-row-${i}`}>
                      <td className="py-2 pr-3 font-semibold text-slate-700">{r.clientName || "—"}</td>
                      <td className="py-2 pr-3 text-slate-500">{regionLabel(r.region, zh)}</td>
                      <td className="py-2 pr-3"><span className="text-[12px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-600">{r.reason}</span></td>
                      <td className="py-2 pr-3 text-slate-500 max-w-[220px] truncate">{r.note || "—"}</td>
                      <td className="py-2 pr-3 text-slate-400">{r.by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </ResponsiveTable>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
